import { compact, defaultsDeep, isEmpty } from "lodash";
import { captureErrorAsync } from "./capture-error-async";
import { omitDeepBy } from "./omit-deep.by";
import { analyticsFullyLoaded } from "./ready";

export const analyticsTrack = (
  eventName: string,
  props: Record<string, unknown> | undefined,
  opts?: SegmentAnalytics.SegmentOpts,
  cb?: () => void
): void => {
  const ctx = defaultsDeep({}, window.analytics._defaultContext, opts);

  if (props && props["application"]) {
    ctx.app = { ...ctx.app, name: props["application"] };

    if (ctx.timestamp == null) {
      ctx.timestamp = new Date().toISOString();
    }
  }

  const args = compact([props, isEmpty(ctx) ? undefined : ctx, cb]);

  return window.analytics.track(eventName, ...args);
};

export function setAnalyticsContext<T extends Record<string, unknown>>(
  item: T
): T & { [k: string]: unknown } {
  const ret = defaultsDeep({}, window.analytics._defaultContext, item);
  window.analytics._defaultContext = ret;

  return ret;
}

export function setDefaultTraits(
  item: Partial<typeof window.analytics._defaultTraits>
): Partial<typeof window.analytics._defaultTraits> {
  const ret = defaultsDeep({}, window.analytics._defaultTraits, item);
  window.analytics._defaultTraits = ret;

  return ret;
}

export function defaultTraits(): Partial<
  typeof window.analytics._defaultTraits
> {
  return window.analytics?._defaultTraits || {};
}

function tryVal<T extends () => Record<string, unknown>>(cb: T) {
  try {
    return cb() || {};
  } catch (e) {
    captureErrorAsync(e);
    return {};
  }
}

async function extractFloorlyticsBlobData() {
  try {
    await analyticsFullyLoaded();

    return omitDeepBy(
      {
        // @ts-ignore
        traits: tryVal(() => window.analytics.user().traits()),
        // @ts-ignore
        context: tryVal(() => window.analytics._defaultContext),
        user: tryVal(() => ({
          id: window.analytics.user().id(),
          anonymousId: window.analytics.user().anonymousId(),
        })),
      },
      (v) => v == null
    );
  } catch (e) {
    captureErrorAsync(e);
    return { traits: {}, context: {}, user: {} };
  }
}

/**
 * Serializes floorlytics data to be consumed later
 */
export async function floorlyticsBlob(): Promise<string> {
  const blobData = await extractFloorlyticsBlobData();
  const data = { version: 1, ...blobData };
  const jsonString = JSON.stringify(data);

  return btoa(jsonString);
}
